// ─────────────────────────────────────────────────────────────────────────────
// V2Onboarding — first-run setup in the v2 design (owners only; shown by OnboardingGate).
// Same data and the same single save as v1: POST /api/auth/getuser to prefill, then
// PUT /api/auth/updateuser { ...form, onboardingComplete: true } at the end.
// Same required fields as v1: company name, state, phone, business type, industry.
// Illustrations: /illustrations/onboarding/ob-*.svg (static, tinted to the accent via CSS).
// Logo: not collected — there is no upload endpoint and the portal expects logoUrl to be a URL
// (v1's picker was preview-only and never saved). ob-brand.svg is reserved for that step.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./v2.css";
import "./v2-pages.css";
import API_BASE from "../apiConfig";
import usePageTitle from "../hooks/usePageTitle";
import { useAppearance } from "./appearance";
import { Glyph } from "./icons";
import { Field, Combo } from "./kit";

const auth = () => ({ "Content-Type": "application/json", "auth-token": localStorage.getItem("token") });
const ART = (n) => `${process.env.PUBLIC_URL || ""}/illustrations/onboarding/ob-${n}.svg`;

// GST state codes → the exact state names v1 stores.
const STATE_BY_CODE = {
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand", "06": "Haryana",
  "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland",
  "14": "Manipur", "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal", "20": "Jharkhand",
  "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat", "25": "Dadra and Nagar Haveli and Daman and Diu",
  "26": "Dadra and Nagar Haveli and Daman and Diu", "27": "Maharashtra", "29": "Karnataka", "30": "Goa", "31": "Lakshadweep",
  "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry", "35": "Andaman and Nicobar Islands", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh",
};
const STATES = [...new Set(Object.values(STATE_BY_CODE))].sort();
const BUSINESS_TYPES = ["Sole Proprietorship", "Partnership Firm", "LLP", "Private Limited Company", "Public Limited Company", "One Person Company", "HUF", "Trust / Society", "Other"];
const INDUSTRIES = ["Trading / Distribution", "Manufacturing", "Retail", "Services", "Construction", "Healthcare / Pharma", "IT / Software", "Food & Beverages", "Textile / Apparel", "Electrical / Electronics", "Publishing / Media", "Education", "Logistics", "Other"];
const RX = { gstin: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/ };

const STEPS = [
  { id: "welcome", art: "welcome" },
  { id: "business", art: "business", eyebrow: "Your business", title: "Tell us about your business", sub: "This is how you'll appear on every invoice." },
  { id: "tax", art: "tax", eyebrow: "Tax & registration", title: "GST details", sub: "Add your GSTIN and we'll fill in the rest. Not registered? Just pick your state." },
  { id: "contact", art: "contact", eyebrow: "Contact", title: "How clients reach you", sub: "Shown on invoices and your customer portal." },
  { id: "review", art: "review", eyebrow: "Review", title: "Looks good?", sub: "Here's how your invoices will introduce you. You can change any of this later in My profile." },
  { id: "done", art: "done" },
];
const FORM_STEPS = ["business", "tax", "contact", "review"]; // shown in the progress indicator

export default function V2Onboarding({ onComplete }) {
  usePageTitle("Welcome");
  useAppearance(); // fonts, backdrop and accent follow the user's v2 appearance
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [name, setName] = useState("");
  const [form, setForm] = useState({ companyName: "", companyGSTIN: "", companyPAN: "", companyState: "", companyAddress: "", phone: "", website: "", businessType: "", industry: "" });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [autoNote, setAutoNote] = useState("");
  const panel = useRef(null);
  const step = STEPS[i];

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/getuser`, { method: "POST", headers: auth() }).then((r) => r.json()).then((d) => {
      if (d.name) setName(d.name.split(" ")[0]);
      setForm((p) => ({ ...p, ...Object.fromEntries(Object.keys(p).map((k) => [k, d[k] || p[k]])) }));
    }).catch(() => {});
  }, []);
  useEffect(() => { setTouched(false); setErr(""); const f = panel.current?.querySelector("input, textarea, button.v2ob-chip"); if (f && step.id !== "welcome") setTimeout(() => f.focus({ preventScroll: true }), 380); }, [i]); // eslint-disable-line

  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setForm((p) => ({ ...p, [k]: v })); };
  const onGSTIN = (e) => {
    const g = e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 15);
    setForm((p) => {
      const n = { ...p, companyGSTIN: g };
      if (RX.gstin.test(g)) {
        const st = STATE_BY_CODE[g.slice(0, 2)]; const pan = g.slice(2, 12); const filled = [];
        if (st && p.companyState !== st) { n.companyState = st; filled.push("state"); }
        if (!p.companyPAN || p.companyPAN !== pan) { n.companyPAN = pan; filled.push("PAN"); }
        setAutoNote(filled.length ? `Filled ${filled.join(" and ")} from your GSTIN` : "");
      } else setAutoNote("");
      return n;
    });
  };
  const missing = useMemo(() => ({
    business: [!form.companyName.trim() && "companyName", !form.businessType && "businessType", !form.industry && "industry"].filter(Boolean),
    tax: [!form.companyState && "companyState"].filter(Boolean),
    contact: [form.phone.replace(/\D/g, "").length < 10 && "phone"].filter(Boolean),
  }), [form]);
  const gstWarn = form.companyGSTIN && !RX.gstin.test(form.companyGSTIN) ? "That doesn't look like a valid GSTIN (15 characters, e.g. 22AAAAA0000A1Z5)." : "";
  const panWarn = form.companyPAN && !RX.pan.test(form.companyPAN) ? "PAN should look like ABCDE1234F." : "";
  const bad = (k) => touched && (missing[step.id] || []).includes(k);

  const go = (to) => { setDir(to > i ? 1 : -1); setI(to); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = async () => {
    if ((missing[step.id] || []).length) { setTouched(true); setErr("Please fill in the highlighted fields."); return; }
    if (step.id === "review") return save();
    go(i + 1);
  };
  const back = () => i > 0 && step.id !== "done" && go(i - 1);
  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/auth/updateuser`, { method: "PUT", headers: auth(), body: JSON.stringify({ ...form, onboardingComplete: true }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save your details. Please try again.");
      go(STEPS.length - 1);
    } catch (x) { setErr(x.message); } finally { setSaving(false); }
  };
  const finish = (to) => { if (onComplete) onComplete(to); else navigate(to || "/dashboard/home"); };

  // Enter = continue, Esc = back (not while typing in the address box or a picker is open)
  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector(".v2k-combo-panel, .v2-glass-menu[data-state='open']")) return;
      if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA" && step.id !== "done" && !(e.target.tagName === "BUTTON" && !e.target.classList.contains("v2ob-next"))) { e.preventDefault(); if (!saving) next(); }
      if (e.key === "Escape" && e.target.tagName !== "INPUT") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const formIdx = FORM_STEPS.indexOf(step.id);
  const chips = (k, options) => (
    <div className={`v2ob-chips${bad(k) ? " is-bad" : ""}`} role="radiogroup">
      {options.map((o) => <button key={o} type="button" role="radio" aria-checked={form[k] === o} className={`v2ob-chip${form[k] === o ? " is-on" : ""}`} onClick={() => setForm((p) => ({ ...p, [k]: o }))}>{o}</button>)}
    </div>
  );

  return (
    <div className="v2 v2ob">
      <div className="v2-backdrop" aria-hidden="true" />
      <img className="v2ob-blob" src={ART("bg-blob")} alt="" aria-hidden="true" />
      <header className="v2ob-top">
        <span className="v2ob-brand"><span className="v2ob-brand-mark">e</span>EazyBilling</span>
        {formIdx >= 0 && (
          <ol className="v2ob-progress" aria-label={`Step ${formIdx + 1} of ${FORM_STEPS.length}`}>
            {FORM_STEPS.map((s, n) => <li key={s} className={n < formIdx ? "is-done" : n === formIdx ? "is-now" : ""}><span /></li>)}
          </ol>
        )}
        <span className="v2ob-top-end">{formIdx >= 0 ? `${formIdx + 1} of ${FORM_STEPS.length}` : ""}</span>
      </header>

      <main className={`v2ob-stage v2ob-stage--${step.id}`}>
        <div className="v2ob-art" key={`art-${i}`} data-dir={dir}>
          <img src={ART(step.art)} alt="" aria-hidden="true" />
        </div>
        <section ref={panel} className="v2ob-panel" key={`panel-${i}`} data-dir={dir} aria-live="polite">
          {step.id === "welcome" && <>
            <span className="v2k-eyebrow">Welcome{name ? `, ${name}` : ""}</span>
            <h1 className="v2ob-title v2ob-title--hero">Let's set up your business.</h1>
            <p className="v2ob-sub">Three quick steps — about two minutes — and you'll be ready to send your first GST invoice.</p>
            <ul className="v2ob-points">
              <li><Glyph name="building" size={18} /> Your business details</li>
              <li><Glyph name="gst" size={18} /> GST registration</li>
              <li><Glyph name="phone" size={18} /> How clients reach you</li>
            </ul>
            <div className="v2ob-actions"><button type="button" className="v2-btn v2-btn--primary v2ob-next v2ob-cta" onClick={() => go(1)} autoFocus>Get started <Glyph name="chevronRight" size={16} stroke={2.2} /></button></div>
          </>}

          {step.title && step.id !== "done" && <>
            <span className="v2k-eyebrow">{step.eyebrow}</span>
            <h1 className="v2ob-title">{step.title}</h1>
            <p className="v2ob-sub">{step.sub}</p>
          </>}

          {step.id === "business" && <div className="v2-form">
            <Field label="Business name" error={bad("companyName") ? "Required" : ""}><input className={`v2-input v2ob-input-lg${bad("companyName") ? " is-bad" : ""}`} value={form.companyName} onChange={set("companyName")} placeholder="e.g. Sharma Traders Pvt. Ltd." autoComplete="organization" /></Field>
            <Field label="Business type" error={bad("businessType") ? "Choose one" : ""}>{chips("businessType", BUSINESS_TYPES)}</Field>
            <Field label="Industry" error={bad("industry") ? "Choose one" : ""}>{chips("industry", INDUSTRIES)}</Field>
          </div>}

          {step.id === "tax" && <div className="v2-form">
            <Field label="GSTIN" optional error={gstWarn} hint={autoNote || "Leave blank if you're not GST-registered"}>
              <input className="v2-input v2ob-input-lg v2ob-mono" value={form.companyGSTIN} onChange={onGSTIN} placeholder="22AAAAA0000A1Z5" maxLength={15} spellCheck={false} />
            </Field>
            {autoNote && <div className="v2ob-auto"><Glyph name="sparkle" size={15} /> {autoNote}</div>}
            <div className="v2-form-row">
              <Field label="State" error={bad("companyState") ? "Required" : ""}>
                <Combo items={STATES.map((s) => ({ id: s, label: s }))} value={form.companyState} onChange={set("companyState")} placeholder="Choose your state" searchPlaceholder="Search states" ariaLabel="State" invalid={bad("companyState")} />
              </Field>
              <Field label="PAN" optional error={panWarn}><input className="v2-input v2ob-mono" value={form.companyPAN} onChange={(e) => set("companyPAN")(e.target.value.toUpperCase().slice(0, 10))} placeholder="ABCDE1234F" maxLength={10} spellCheck={false} /></Field>
            </div>
          </div>}

          {step.id === "contact" && <div className="v2-form">
            <Field label="Phone" error={bad("phone") ? "Enter a 10-digit number" : ""}>
              <span className="v2-input-wrap"><span className="v2-input-prefix v2ob-prefix">+91</span><input className={`v2-input v2ob-input-lg${bad("phone") ? " is-bad" : ""}`} style={{ paddingLeft: 52 }} type="tel" inputMode="numeric" autoComplete="tel-national" value={form.phone} onChange={set("phone")} placeholder="98765 43210" /></span>
            </Field>
            <Field label="Address on invoices" optional><textarea className="v2-input v2ob-textarea" rows={3} value={form.companyAddress} onChange={set("companyAddress")} placeholder="Building, street, area, city, pincode" autoComplete="street-address" /></Field>
            <Field label="Website" optional><input className="v2-input" type="url" value={form.website} onChange={set("website")} placeholder="https://yourbusiness.com" autoComplete="url" /></Field>
          </div>}

          {step.id === "review" && <div className="v2ob-review">
            <div className="v2ob-preview" aria-label="Invoice header preview">
              <span className="v2ob-preview-mark">{(form.companyName || "?").trim().slice(0, 1).toUpperCase()}</span>
              <div className="v2ob-preview-text">
                <strong>{form.companyName || "Your business"}</strong>
                {form.companyAddress && <span>{form.companyAddress}</span>}
                <span className="v2ob-small">{[form.companyGSTIN && `GSTIN ${form.companyGSTIN}`, form.phone && `+91 ${form.phone}`].filter(Boolean).join("  ·  ") || form.companyState}</span>
              </div>
              <span className="v2ob-preview-doc">Tax Invoice<b>INV-0001</b></span>
            </div>
            <dl className="v2ob-summary">
              {[["Business", [form.companyName, form.businessType, form.industry], 1], ["Tax", [form.companyGSTIN || "Not GST-registered", form.companyPAN && `PAN ${form.companyPAN}`, form.companyState], 2], ["Contact", [form.phone && `+91 ${form.phone}`, form.website], 3]].map(([label, vals, to]) => (
                <div key={label}><dt>{label}</dt><dd>{vals.filter(Boolean).join(" · ") || "—"}</dd><button type="button" className="v2k-link" onClick={() => go(to)}>Edit</button></div>))}
            </dl>
          </div>}

          {step.id === "done" && <>
            <span className="v2k-eyebrow">All set{name ? `, ${name}` : ""}</span>
            <h1 className="v2ob-title v2ob-title--hero">You're ready to bill.</h1>
            <p className="v2ob-sub">{form.companyName || "Your business"} is set up. Here's a good place to start:</p>
            <div className="v2ob-next-steps">
              {[["invoice", "Create your first invoice", "GST worked out for you", "/dashboard/createinvoice"], ["clients", "Add a client", "Save their GSTIN and address once", "/dashboard/manageclients"], ["bank", "Add bank details", "So clients know how to pay", "/dashboard/addbankdetails"]].map(([g, t, s, to], n) => (
                <button key={to} type="button" className="v2ob-nextcard" style={{ animationDelay: `${120 + n * 70}ms` }} onClick={() => finish(to)}>
                  <span className="v2ob-nextcard-ico"><Glyph name={g} size={20} /></span><span><b>{t}</b><span>{s}</span></span><Glyph name="chevronRight" size={16} stroke={2} />
                </button>))}
            </div>
            <div className="v2ob-actions"><button type="button" className="v2-btn" onClick={() => finish()} autoFocus>Go to dashboard</button></div>
          </>}

          {err && <div className="v2-banner v2-banner--bad v2ob-err"><Glyph name="alert" size={16} /> {err}</div>}

          {formIdx >= 0 && (
            <div className="v2ob-actions">
              <button type="button" className="v2-btn" onClick={back}><Glyph name="chevronLeft" size={16} stroke={2.2} /> Back</button>
              <button type="button" className="v2-btn v2-btn--primary v2ob-next" disabled={saving} onClick={next}>
                {step.id === "review" ? (saving ? "Saving…" : "Finish setup") : "Continue"} {!saving && <Glyph name={step.id === "review" ? "check" : "chevronRight"} size={16} stroke={2.2} />}
              </button>
              <span className="v2ob-keyhint"><kbd>Enter</kbd> to continue</span>
            </div>)}
        </section>
      </main>
    </div>
  );
}
